import { IOrderType } from "./admin";

export interface OrderResponse {
  id: number;
  type: "basic" | "inventory" | "custom" | "thermal" | "";
  organization_order_type_id?: number;
  submitted: string | null;
  description: string;
  survey_id: number;
  card: string;
  completed: string | null;
  started: string | null;
  scheduled_at: string | null;
  created_by: number;
  created_by_name: string;
  submitted_by_name: string | null;
}

/** True when the order has been submitted or scheduled for submission. */
export function isOrderSubmitted(
  order: Pick<OrderResponse, "submitted" | "scheduled_at"> | null | undefined
): boolean {
  return Boolean(order?.submitted || order?.scheduled_at);
}

interface CreateOrderResponse {
  order: {
    id: number;
    type: "basic" | "inventory" | "custom" | "thermal";
    organization_order_type_id?: number;
  };
}

interface UpdateOrderResponse {
  order: OrderResponse;
}

interface CancelOrderResponse {
  success: boolean;
}

class ApiOrders {
  public static getOrders(surveyId: number): Promise<OrderResponse[]> {
    return fetch(`/api/v1/surveys/${surveyId}/orders`, {
      method: "GET",
    }).then((response) => {
      if (!response.ok) {
        throw new Error(
          `Failed to fetch orders: ${response.status} ${response.statusText}`
        );
      }
      return response.json() as Promise<OrderResponse[]>;
    });
  }

  public static createOrder(
    surveyId: number,
    params: {
      organization_order_type_id: number;
    }
  ): Promise<CreateOrderResponse> {
    const createOrderRoute = `/api/v1/surveys/${surveyId}/orders`;
    const { organization_order_type_id } = params;

    return fetch(createOrderRoute, {
      method: "POST",
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ organization_order_type_id }),
    }).then((response) => {
      if (!response.ok) {
        throw new Error(
          `Failed to create order: ${response.status} ${response.statusText}`
        );
      }
      return response.json() as Promise<CreateOrderResponse>;
    });
  }

  public static updateOrder(
    surveyId: number,
    params: {
      description: string;
      files?: File[];
      source: "auto" | "manual";
    }
  ): Promise<UpdateOrderResponse> {
    const updateOrderRoute = `/api/v1/surveys/${surveyId}/orders`;
    const { description, files, source } = params;

    const formData = new FormData();

    formData.append("description", description);
    formData.append("source", source);

    files.forEach((file) => formData.append("attachments", file));

    return fetch(updateOrderRoute, {
      method: "PUT",
      body: formData,
    }).then((response) => {
      if (!response.ok) {
        throw new Error(
          `Failed to update order: ${response.status} ${response.statusText}`
        );
      }
      return response.json() as Promise<UpdateOrderResponse>;
    });
  }

  public static updateOrderType(
    surveyId: number,
    orderId: number,
    organizationOrderTypeId: number
  ): Promise<CreateOrderResponse> {
    return fetch(`/api/v1/surveys/${surveyId}/orders/${orderId}`, {
      method: "PUT",
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ organization_order_type_id: organizationOrderTypeId }),
    }).then((response) => {
      if (!response.ok) {
        throw new Error(
          `Failed to update order type: ${response.status} ${response.statusText}`
        );
      }
      return response.json() as Promise<CreateOrderResponse>;
    });
  }

  public static cancelScheduledOrder(
    surveyId: number,
    orderId: number
  ): Promise<CancelOrderResponse> {
    const cancelOrderRoute = `/api/v1/surveys/${surveyId}/orders/${orderId}/cancel`;

    return fetch(cancelOrderRoute, {
      method: "POST",
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json",
      },
    }).then((response) => {
      if (!response.ok) {
        throw new Error(
          `Failed to cancel scheduled order: ${response.status} ${response.statusText}`
        );
      }
      return response.json() as Promise<CancelOrderResponse>;
    });
  }

  public static getOrderTypesForFolder(
    folderId: number
  ): Promise<IOrderType[]> {
    return fetch(`/api/v1/folders/${folderId}/order-types`, {
      method: "GET",
    }).then((response) => {
      if (!response.ok) {
        throw new Error(
          `Failed to fetch order types: ${response.status} ${response.statusText}`
        );
      }
      return response.json() as Promise<IOrderType[]>;
    });
  }
}

export default ApiOrders;
